import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { siteApi } from '@/api';
import type { PublicPlan, PublicSiteContent, SiteSettings } from '@/api/types';

/**
 * Branding, contact details and plan prices, fetched once for the whole app.
 *
 * Before this existed, the phone number lived in three files, the app name in
 * a translation key, and the plan prices in a *fourth* place — so changing
 * what you charge meant editing code and deploying, and the landing page
 * could disagree with what the platform actually billed.
 *
 * The call is unauthenticated because the landing page needs it before anyone
 * signs in. It returns nothing private: names, prices, and the contact details
 * already printed at the bottom of every page.
 */
const FALLBACK: SiteSettings = {
  appNameAr: 'عمارتي',
  appNameEn: 'Omarti',
  taglineAr: '',
  taglineEn: '',
  supportPhone: '',
  supportWhatsapp: '',
  supportEmail: '',
  addressAr: '',
  addressEn: '',
  hoursAr: '',
  hoursEn: '',
  social: { facebook: null, instagram: null, x: null, linkedin: null, youtube: null, tiktok: null },
  legalNoteAr: '',
  legalNoteEn: '',
};

type SiteValue = {
  settings: SiteSettings;
  plans: PublicPlan[];
  tax: PublicSiteContent['tax'];
  loading: boolean;
  reload: () => void;
};

const SiteContext = createContext<SiteValue>({
  settings: FALLBACK,
  plans: [],
  tax: { isEnabled: false, rateBasisPoints: 0, labelAr: '', labelEn: '' },
  loading: true,
  reload: () => undefined,
});

export const useSite = () => useContext(SiteContext);

export function SiteProvider({ children }: { children: ReactNode }) {
  const [content, setContent] = useState<PublicSiteContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    void siteApi
      .content()
      .then((next) => {
        if (!cancelled) setContent(next);
      })
      // A failed fetch must not blank the header: the fallback keeps the app
      // usable, and the only cost is a generic name until the next load.
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [nonce]);

  const value = useMemo<SiteValue>(
    () => ({
      settings: content?.settings ?? FALLBACK,
      plans: content?.plans ?? [],
      tax: content?.tax ?? { isEnabled: false, rateBasisPoints: 0, labelAr: '', labelEn: '' },
      loading,
      reload: () => setNonce((n) => n + 1),
    }),
    [content, loading],
  );

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}

/** Ready-to-use links, with the empty cases already handled. */
export function useContactLinks() {
  const { settings } = useSite();

  return useMemo(
    () => ({
      whatsapp: settings.supportWhatsapp ? `https://wa.me/${settings.supportWhatsapp}` : null,
      phone: settings.supportPhone ? `tel:+${settings.supportPhone}` : null,
      email: settings.supportEmail ? `mailto:${settings.supportEmail}` : null,
      social: Object.entries(settings.social).filter(([, url]) => Boolean(url)) as [
        keyof SiteSettings['social'],
        string,
      ][],
    }),
    [settings],
  );
}
